import type { PublicEvent } from "../events/schemas";
import type { Membership } from "../event-experience/schemas";
import { eventHasEnded } from "./event-end";
import {
  useLiveMemberTrack,
  useOwnTrackSwitchRequest,
  useTrackSwitchWrites,
  type OwnRequestState,
  type TrackSwitchWrites,
} from "./use-track-switch";

export interface AttendeeTrackSwitch {
  /** The organiser has the feature on and this member has a roster row. */
  enabled: boolean;
  /** New requests are taken: enabled and the event has not ended. */
  accepting: boolean;
  /** The member's track as it reads now (live while the feature is on). */
  myTrackId?: string;
  request: OwnRequestState;
  writes: TrackSwitchWrites;
}

/**
 * Everything the attendee pages need for table requests (TNG-92), in one
 * place so the Overview card and the agenda banner agree.
 *
 * With the feature off nothing is read beyond what the page already had, and
 * `myTrackId` is the membership's own value.
 */
export function useAttendeeTrackSwitch(
  event: PublicEvent,
  membership: Membership,
  now: Date = new Date(),
): AttendeeTrackSwitch {
  const featureOn = event.trackSwitchEnabled === true;
  const enabled = featureOn && membership.attendeeId !== undefined;
  const live = useLiveMemberTrack(event.eventId, membership.uid, featureOn);
  const request = useOwnTrackSwitchRequest(
    event.eventId,
    membership.attendeeId,
    enabled,
  );
  const writes = useTrackSwitchWrites(
    event.eventId,
    membership.uid,
    membership.attendeeId,
  );
  return {
    enabled,
    accepting: enabled && !eventHasEnded(event.endDate, event.timezone, now),
    myTrackId: live.loaded ? live.trackId : membership.trackId,
    request,
    writes,
  };
}
